import { type InterestAssetMatchScore } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useRef, useState } from 'react'
import { View } from 'react-native'
import Animated, { FadeInDown, FadeOutDown } from 'react-native-reanimated'
import { MatchBadge } from '~/components/ui/match-badge'
import { cn } from '~/lib/utils'

const normalizeScore = (score: number | null | undefined): number | null => {
  if (score === null || score === undefined) return null
  return Math.round(Math.min(Math.max(score, 0), 4))
}

export function InterestMatchBadge({
  scores,
  label: customLabel,
  className,
  breakdownClassName,
  buttonClassName,
  badgeClassName,
  variant = 'default',
}: {
  scores: InterestAssetMatchScore | null | undefined
  label?: string
  className?: string
  breakdownClassName?: string
  buttonClassName?: string
  badgeClassName?: string
  variant?: 'default' | 'inverted'
}) {
  const matchScore = normalizeScore(scores?.match_score)
  const locationScore = normalizeScore(scores?.location_score)
  const priceScore = normalizeScore(scores?.price_score)
  const sizeScore = normalizeScore(scores?.size_score)
  const buildingTypeScore = normalizeScore(scores?.building_type_score)
  const otherScore = normalizeScore(scores?.other_score)

  const { t } = useTranslations('interest')
  const [showBreakdown, setShowBreakdown] = useState(false)

  const isOverTrigger = useRef(false)
  const didShowByPress = useRef(false)

  return (
    <View className={cn('relative items-start', className)}>
      {showBreakdown && (
        <Animated.View
          entering={FadeInDown.duration(150)}
          exiting={FadeOutDown.duration(150)}
          className={cn('absolute -right-1 bottom-full mb-1 items-end gap-1', breakdownClassName)}>
          <MatchBadge score={locationScore} variant="default" label={t(($) => $.labels.location)} />
          <MatchBadge score={priceScore} variant="default" label={t(($) => $.labels.budget)} />
          <MatchBadge score={sizeScore} variant="default" label={t(($) => $.labels.sizeLabel)} />
          <MatchBadge score={buildingTypeScore} variant="default" label={t(($) => $.labels.type)} />
          <MatchBadge score={otherScore} variant="default" label={t(($) => $.labels.other)} />
        </Animated.View>
      )}
      <MatchBadge
        score={matchScore}
        variant={variant}
        label={customLabel}
        onPress={() => {
          didShowByPress.current = true
          setShowBreakdown((s) => !s)
        }}
        onPointerEnter={() => {
          isOverTrigger.current = true

          setTimeout(() => {
            if (isOverTrigger.current) {
              didShowByPress.current = false
              setShowBreakdown(true)
            }
          }, 300)
        }}
        onPointerLeave={() => {
          isOverTrigger.current = false

          setTimeout(() => {
            if (!isOverTrigger.current && !didShowByPress.current) {
              setShowBreakdown(false)
            }
          }, 150)
        }}
        className={cn(badgeClassName, buttonClassName)}
      />
    </View>
  )
}
